import LinkCard from '@/components/patterns/link-card'
import { CardContent } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import { usePathHelper } from '@/hooks/usePathHelper'
import { cn } from '@/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { CircleOff, Mail, MapPin, Phone, ShoppingBasket, Truck } from 'lucide-react'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { BadgeButton } from '@/components/ui/badge'

export default function LocationCard({ location, className, to, params, disabled = false }) {
  // Format the full address on one line
  const formatAddress = (address) => {
    const parts = [
      address.line1,
      address.line2,
      `${address.city}, ${address.state} ${address.postal_code}`,
    ].filter(Boolean)

    return parts.join(', ')
  }

  const path = usePathHelper()
  const navigate = useNavigate()

  const { formatPhone } = usePhoneFormatter()

  return (
    <LinkCard
      to={to}
      params={params}
      disabled={disabled}
      className={cn(
        'overflow-hidden relative transition-all',
        !location.is_active && 'bg-muted/25',
        className
      )}
    >
      <CardContent className="space-y-4">
        {(!location.is_active || location.is_default) && (
          <div
            className={cn(
              'absolute min-w-16 justify-center items-center right-0 top-0 z-10 rounded-bl-md px-2 py-1',
              location.is_default
                ? 'bg-secondary text-secondary-foreground'
                : 'bg-muted text-muted-foreground'
            )}
          >
            <Typography variant="label">{location.is_default ? 'Default' : 'Inactive'}</Typography>
          </div>
        )}
        <div className="grid">
          <div>
            <Typography variant="emphasis" tone="inherit">
              {location.address.name}
            </Typography>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <Typography variant="label" tone="muted" className="break-words">
              {formatAddress(location.address)}
            </Typography>
          </div>

          <div className="flex items-center gap-3 text-muted-foreground">
            <Phone className="h-4 w-4 flex-shrink-0" />
            <Typography variant="label" className="transition-colors break-all">
              {formatPhone(location.phone, location.phone_country_code)}
            </Typography>
          </div>

          <div className="flex items-center gap-3 text-muted-foreground">
            <Mail className="h-4 w-4 flex-shrink-0" />
            <Typography variant="label" className="transition-colors break-all">
              {location.email ?? 'No email address'}
            </Typography>
          </div>

          <IfGlobalOrg show={false}>
            <div className="flex items-center gap-3 text-muted-foreground mt-6">
              {[
                {
                  active: location.is_pickup,
                  label: 'Pickup',
                  icon: location.is_pickup ? <ShoppingBasket /> : <CircleOff />,
                  to: path.hours.index().using({ locationId: location.id }).link,
                },
                {
                  active: location.is_delivery,
                  label: 'Delivery',
                  icon: location.is_delivery ? <Truck /> : <CircleOff />,
                  to: path.deliveries.index().using({ locationId: location.id }).link,
                },
              ].map(({ active, label, icon, to }) => (
                <BadgeButton
                  key={label}
                  variant={active ? 'brand' : 'muted'}
                  size="comfortable"
                  onClick={(event) => {
                    event.preventDefault()
                    void navigate({ ...to })
                  }}
                >
                  {icon}
                  {label}
                </BadgeButton>
              ))}
            </div>
          </IfGlobalOrg>
        </div>
      </CardContent>
    </LinkCard>
  )
}
